import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isRecurring,
  isStoryRefreshValid,
  validateStoryRefresh,
  type RepeatPreset,
  type StoryRefreshConfig,
  type StoryRefreshErrors,
  type Weekday,
} from "../../../utils/storyRefreshTypes";
import { isoDateTodayLocal } from "../../../utils/scheduleTypes";
import {
  dayOfMonthFromIso,
  defaultMonthlyRepeatOn,
} from "../../../utils/storyRefreshConstants";

/** Dropdown identifiers — only one menu in the modal is open at a time. */
export type StoryRefreshDropdownKey =
  | "repeat"
  | "repeatDom"
  | "hour"
  | "timezone";

const defaults = (): StoryRefreshConfig => ({
  repeat: "none",
  every: 1,
  repeatOn: [],
  repeatDom: null,
  monthlyRepeatOn: null,
  scheduleOn: isoDateTodayLocal(),
  startsOn: isoDateTodayLocal(),
  refreshHour: 10,
  timezone: "UTC",
  endsMode: "never",
  endsOn: "",
  endsAfter: 12,
});

const normalize = (config: StoryRefreshConfig): StoryRefreshConfig => {
  if (config.repeat === "monthly") {
    const startsOn = config.startsOn || isoDateTodayLocal();
    return {
      ...config,
      startsOn,
      repeatOn: [],
      repeatDom: dayOfMonthFromIso(startsOn),
      monthlyRepeatOn:
        config.monthlyRepeatOn ?? defaultMonthlyRepeatOn(startsOn),
    };
  }

  // `does_not_repeat` and `daily` carry no `ends` on the wire, so the form must
  // not hold one either. Weekly and monthly both render all three end modes and
  // keep whatever the server sent — a saved "After 12" must come back selected.
  if (!isRecurring(config.repeat) && config.endsMode !== "never") {
    return { ...config, endsMode: "never", endsOn: "", endsAfter: null };
  }

  return config;
};

const merge = (
  initial: Partial<StoryRefreshConfig> | undefined,
): StoryRefreshConfig => normalize({ ...defaults(), ...(initial ?? {}) });

/**
 * Reset the fields a preset does not render, and seed the ones it does.
 *
 * Without this, switching Weekly → Monthly would submit the weekday list the
 * user picked under Weekly — invisible in the form, but very much in the
 * payload. Validation ignores hidden fields for the same reason; this makes
 * the config itself honest rather than relying on the reader to know which
 * fields are live.
 */
const applyPreset = (
  prev: StoryRefreshConfig,
  repeat: RepeatPreset,
): StoryRefreshConfig => {
  const base = defaults();

  if (repeat === "none") {
    return {
      ...prev,
      repeat,
      every: base.every,
      repeatOn: [],
      repeatDom: null,
      monthlyRepeatOn: null,
      startsOn: base.startsOn,
      timezone: base.timezone,
      endsMode: "never",
      endsOn: "",
      endsAfter: null,
      scheduleOn: prev.scheduleOn || base.scheduleOn,
    };
  }

  if (repeat === "daily") {
    return {
      ...prev,
      repeat,
      every: base.every,
      repeatOn: [],
      repeatDom: null,
      monthlyRepeatOn: null,
      startsOn: base.startsOn,
      timezone: base.timezone,
      endsMode: "never",
      endsOn: "",
      endsAfter: null,
    };
  }

  if (repeat === "weekly") {
    return {
      ...prev,
      repeat,
      repeatDom: null,
      monthlyRepeatOn: null,
      endsAfter: prev.endsAfter ?? base.endsAfter,
      startsOn: prev.startsOn || base.startsOn,
    };
  }

  // monthly — Repeat on options are derived from Starts on.
  const startsOn = prev.startsOn || base.startsOn;
  return {
    ...prev,
    repeat,
    repeatOn: [],
    startsOn,
    repeatDom: dayOfMonthFromIso(startsOn),
    endsAfter: prev.endsAfter ?? base.endsAfter,
    monthlyRepeatOn:
      prev.monthlyRepeatOn ?? defaultMonthlyRepeatOn(startsOn),
  };
};

export interface UseStoryScheduleRefreshArgs {
  /** Reset to this value whenever `open` flips false → true. */
  initialValue?: Partial<StoryRefreshConfig>;
  open: boolean;
}

export interface UseStoryScheduleRefreshResult {
  config: StoryRefreshConfig;
  errors: StoryRefreshErrors;
  /** True once the user has attempted to submit — gates error display. */
  showErrors: boolean;
  isValid: boolean;
  openDropdown: StoryRefreshDropdownKey | null;
  timezoneQuery: string;
  patch: (next: Partial<StoryRefreshConfig>) => void;
  setRepeatPreset: (repeat: RepeatPreset) => void;
  toggleWeekday: (day: Weekday) => void;
  openDropdownKey: (key: StoryRefreshDropdownKey) => void;
  setTimezoneQuery: (query: string) => void;
  closeDropdown: () => void;
  /** Marks the form touched. Returns whether the config is valid. */
  attemptSubmit: () => boolean;
}

/**
 * State + validation for the story "Schedule refresh" modal.
 *
 * Mirrors `useScheduleRefresh` (the connector equivalent) including its
 * reset-on-reopen behaviour, and adds `setRepeatPreset` because this form's
 * shape is preset-driven.
 */
export const useStoryScheduleRefresh = ({
  initialValue,
  open,
}: UseStoryScheduleRefreshArgs): UseStoryScheduleRefreshResult => {
  const [config, setConfig] = useState<StoryRefreshConfig>(() =>
    merge(initialValue),
  );
  const [showErrors, setShowErrors] = useState(false);
  const [openDropdown, setOpenDropdown] =
    useState<StoryRefreshDropdownKey | null>(null);
  const [timezoneQuery, setTimezoneQuery] = useState("");

  // Reset on the closed → open transition only. Keying off `initialValue`
  // identity as well would discard the user's edits mid-session: the parent
  // re-fetches the schedule and hands down a fresh config object, which is a
  // new identity every time and would snap the form back to the saved value.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setConfig(merge(initialValue));
      setShowErrors(false);
      setOpenDropdown(null);
      setTimezoneQuery("");
    }
    wasOpen.current = open;
  }, [open, initialValue]);

  useEffect(() => {
    if (openDropdown !== "timezone") setTimezoneQuery("");
  }, [openDropdown]);

  const errors = useMemo(() => validateStoryRefresh(config), [config]);
  const isValid = useMemo(() => isStoryRefreshValid(config), [config]);

  const patch = useCallback((next: Partial<StoryRefreshConfig>) => {
    setConfig((prev) => {
      const merged = { ...prev, ...next };

      // Selecting "After" must seed a count: the stepper renders
      // `endsAfter ?? 1`, so a null behind that display would show a value the
      // config does not hold — and then fail validation as missing.
      if (next.endsMode === "after" && merged.endsAfter === null) {
        merged.endsAfter = defaults().endsAfter;
      }

      if (prev.repeat === "monthly" && next.startsOn !== undefined) {
        return {
          ...merged,
          repeatDom: dayOfMonthFromIso(next.startsOn),
          monthlyRepeatOn: defaultMonthlyRepeatOn(next.startsOn),
        };
      }
      return merged;
    });
  }, []);

  const setRepeatPreset = useCallback((repeat: RepeatPreset) => {
    setConfig((prev) => applyPreset(prev, repeat));
  }, []);

  const toggleWeekday = useCallback((day: Weekday) => {
    setConfig((prev) => ({
      ...prev,
      repeatOn: prev.repeatOn.includes(day)
        ? prev.repeatOn.filter((d) => d !== day)
        : [...prev.repeatOn, day],
    }));
  }, []);

  const openDropdownKey = useCallback(
    (key: StoryRefreshDropdownKey) =>
      setOpenDropdown((current) => (current === key ? null : key)),
    [],
  );

  const closeDropdown = useCallback(() => setOpenDropdown(null), []);

  const attemptSubmit = useCallback((): boolean => {
    setShowErrors(true);
    return isStoryRefreshValid(config);
  }, [config]);

  return {
    config,
    errors,
    showErrors,
    isValid,
    openDropdown,
    timezoneQuery,
    patch,
    setRepeatPreset,
    toggleWeekday,
    openDropdownKey,
    setTimezoneQuery,
    closeDropdown,
    attemptSubmit,
  };
};
